import type { CountryCode } from "@platform/contracts";
import {
  Button,
  EmptyState,
  Note,
  PageHeader,
  Panel,
  SearchInput,
  StatusBadge,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@platform/ui";
import { ErrorText, errorKey, Field, useSession } from "@platform/ui/features";
import { Building2 } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useTranslations } from "use-intl";
import { ConsoleCrumb } from "../components/crumb";
import {
  EMPTY_SCREENING,
  type ScreeningDraft,
  ScreeningFields,
  screeningBody,
  screeningReady,
} from "../components/screening-fields";
import { useStepUp } from "../components/step-up";
import { formatDateTime } from "../lib/i18n";
import { EnterprisePlanPanel } from "./enterprise-plan";

interface EnterpriseRow {
  readonly id: string;
  readonly legalName: string;
  readonly registrationNumber: string;
  readonly country: string;
  readonly verificationStatus: "submitted" | "under_review" | "verified" | "rejected";
  readonly suspension: { readonly at: string; readonly reason: string | null } | null;
  readonly screening?: { readonly at: string; readonly result: "clear" | "possible_match" } | null;
}

/** Whether the operator reviews verification, and so reads enterprises with their cases and may suspend them. */
function useReviews(): boolean {
  const { state } = useSession();
  return state.status === "signed_in" && state.claims.perm.includes("ops:verification.review");
}

/** Whether the operator works support, and so may follow an enterprise to its orders (ADM-5, card 080). */
function useSupports(): boolean {
  const { state } = useSession();
  return state.status === "signed_in" && state.claims.perm.includes("ops:support.read");
}

/** Enterprises by name or registration number (ENT-3); support reads them through its own routes (ADM-5). */
export function EnterprisesPage() {
  const t = useTranslations();
  const { session } = useSession();
  const reviews = useReviews();
  // The console bar's look-up arrives as `?q=` (card 110).
  const [params] = useSearchParams();
  const asked = params.get("q") ?? "";
  // The enterprises of one country, as when a country list changes in the trade settings (card 127).
  const country = params.get("country") ?? undefined;
  const [query, setQuery] = useState(asked);
  const [rows, setRows] = useState<readonly EnterpriseRow[] | undefined>();
  // Reviewers find the verified enterprises due a sanctions screening (ADR 0026).
  const [due, setDue] = useState(false);

  const search = useCallback(
    async (q: string, screeningDue = false) => {
      const filter = { params: { query: q.trim() === "" ? {} : { q: q.trim() } } };
      const { data } = reviews
        ? await session.api.GET("/v1/ops/enterprises", {
            params: {
              query: {
                ...filter.params.query,
                ...(screeningDue ? { screeningDue: "true" as const } : {}),
                ...(country === undefined ? {} : { country: country as CountryCode }),
              },
            },
          })
        : await session.api.GET("/v1/ops/support/enterprises", filter);
      setRows(data?.items ?? []);
    },
    [session, reviews, country],
  );

  useEffect(() => {
    setQuery(asked);
    void search(asked);
  }, [search, asked]);

  return (
    <section aria-labelledby="enterprises-title" className="flex flex-col gap-5">
      <PageHeader
        id="enterprises-title"
        title={t("admin.enterprises.title")}
        description={t("admin.enterprises.intro")}
      />
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void search(query, due);
        }}
      >
        <SearchInput
          aria-label={t("admin.enterprises.search")}
          placeholder={t("admin.enterprises.search")}
          className="w-full max-w-md border-border bg-surface"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="submit" variant="secondary">
          {t("admin.enterprises.searchButton")}
        </Button>
        {reviews && (
          <label className="flex shrink-0 cursor-pointer items-center gap-2 text-small text-ink">
            <input
              type="checkbox"
              className="size-4 accent-brand-600"
              checked={due}
              onChange={(event) => {
                setDue(event.target.checked);
                void search(query, event.target.checked);
              }}
            />
            {t("admin.enterprises.screeningDue")}
          </label>
        )}
      </form>
      {rows !== undefined && (
        <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
          {rows.length === 0 ? (
            <EmptyState icon={Building2} title={t("admin.enterprises.none")} />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("admin.queue.enterprise")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.queue.country")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.queue.registration")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.enterprises.status")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link
                        to={`/enterprises/${row.id}`}
                        className="font-medium text-ink hover:text-brand-600"
                      >
                        {row.legalName}
                      </Link>
                    </TableCell>
                    <TableCell>{row.country}</TableCell>
                    <TableCell className="tabular-nums">{row.registrationNumber}</TableCell>
                    <TableCell>
                      <EnterpriseStatus enterprise={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </section>
  );
}

/** Verification's state, or Suspended when the platform suspended the enterprise. */
function EnterpriseStatus({ enterprise }: { enterprise: EnterpriseRow }) {
  const t = useTranslations();
  return enterprise.suspension === null ? (
    <StatusBadge
      label={t(`admin.status.${enterprise.verificationStatus}`)}
      tone={statusTone("verification", enterprise.verificationStatus)}
    />
  ) : (
    <StatusBadge label={t("admin.enterprises.suspended")} tone="problem" />
  );
}

interface Detail {
  readonly enterprise: EnterpriseRow;
  readonly activeMembers: number;
  readonly cases: readonly {
    readonly id: string;
    readonly decision: string | null;
    readonly createdAt: string;
  }[];
}

/** One enterprise: a reviewer's page with cases, suspension, and reinstatement, or support's read-only one. */
export function EnterprisePage() {
  return useReviews() ? <ReviewerEnterprise /> : <SupportEnterprise />;
}

/** To the enterprise's orders, as buyer or seller, for operators who work support. */
function OrdersLink({ enterpriseId }: { enterpriseId: string }) {
  const t = useTranslations();
  if (!useSupports()) return null;
  return (
    <Link
      to={`/orders?enterpriseId=${enterpriseId}`}
      className="inline-flex items-center gap-1.5 text-small font-medium text-brand-600 hover:text-brand-700"
    >
      {t("admin.enterprises.orders")}
    </Link>
  );
}

/** The enterprise as support reads it: no verification cases, nothing to change (ADM-5, card 080). */
function SupportEnterprise() {
  const t = useTranslations();
  const { enterpriseId = "" } = useParams();
  const { session } = useSession();
  const [detail, setDetail] = useState<Omit<Detail, "cases"> | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void session.api
      .GET("/v1/ops/support/enterprises/{enterpriseId}", { params: { path: { enterpriseId } } })
      .then(({ data, error: problem }) =>
        data === undefined ? setError(t(errorKey(problem))) : setDetail(data),
      );
  }, [session, enterpriseId, t]);

  if (detail === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  return (
    <section aria-labelledby="enterprise-title" className="flex flex-col gap-5">
      <EnterpriseHeader enterprise={detail.enterprise} />
      <EnterpriseFacts enterprise={detail.enterprise} activeMembers={detail.activeMembers} />
      <EnterprisePlanPanel enterpriseId={detail.enterprise.id} />
    </section>
  );
}

function EnterpriseFacts({
  enterprise,
  activeMembers,
}: {
  enterprise: EnterpriseRow;
  activeMembers: number;
}) {
  const t = useTranslations();
  return (
    <Panel title={t("admin.enterprises.facts")} actions={<OrdersLink enterpriseId={enterprise.id} />}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-small">
        <dt className="text-ink-muted">{t("admin.queue.registration")}</dt>
        <dd className="text-ink tabular-nums">{`${enterprise.country} ${enterprise.registrationNumber}`}</dd>
        <dt className="text-ink-muted">{t("admin.enterprises.status")}</dt>
        <dd className="text-ink">{t(`admin.status.${enterprise.verificationStatus}`)}</dd>
        <dt className="text-ink-muted">{t("admin.enterprises.membersLabel")}</dt>
        <dd className="text-ink">{t("admin.enterprises.members", { count: activeMembers })}</dd>
      </dl>
      {enterprise.suspension !== null && (
        <Note tone="problem">
          {t("admin.enterprises.suspendedSince", {
            date: formatDateTime(enterprise.suspension.at),
            reason: enterprise.suspension.reason ?? "",
          })}
        </Note>
      )}
    </Panel>
  );
}

/** The enterprise's name with its state, under the way back to the look-up. */
function EnterpriseHeader({ enterprise }: { enterprise: EnterpriseRow }) {
  const t = useTranslations();
  return (
    <PageHeader
      id="enterprise-title"
      breadcrumb={
        <ConsoleCrumb to="/enterprises" back={t("admin.enterprises.title")} current={enterprise.legalName} />
      }
      title={enterprise.legalName}
      marks={<EnterpriseStatus enterprise={enterprise} />}
    />
  );
}

/** One enterprise with suspend and reinstate, both behind step-up and a reason (ENT-3). */
function ReviewerEnterprise() {
  const t = useTranslations();
  const { enterpriseId = "" } = useParams();
  const { session } = useSession();
  const stepUp = useStepUp();
  const [detail, setDetail] = useState<Detail | undefined>();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/enterprises/{enterpriseId}", {
      params: { path: { enterpriseId } },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setDetail(data);
  }, [session, enterpriseId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    const suspended = detail?.enterprise.suspension != null;
    const outcome = await stepUp.run(() =>
      suspended
        ? session.api.POST("/v1/ops/enterprises/{enterpriseId}/reinstate", {
            params: { path: { enterpriseId } },
            body: { reason: reason.trim() },
          })
        : session.api.POST("/v1/ops/enterprises/{enterpriseId}/suspend", {
            params: { path: { enterpriseId } },
            body: { reason: reason.trim() },
          }),
    );
    if (outcome.error !== undefined) return setError(t(errorKey(outcome.error)));
    setReason("");
    await load();
  };

  if (detail === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const { enterprise } = detail;
  const canChange = enterprise.suspension !== null || enterprise.verificationStatus === "verified";
  return (
    <section aria-labelledby="enterprise-title" className="flex flex-col gap-5">
      <EnterpriseHeader enterprise={enterprise} />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <EnterpriseFacts enterprise={enterprise} activeMembers={detail.activeMembers} />
          <EnterprisePlanPanel enterpriseId={enterprise.id} />
          {enterprise.verificationStatus === "verified" && (
            <ScreeningPanel enterprise={enterprise} onRecorded={() => void load()} />
          )}
          <Panel title={t("admin.enterprises.cases")} flush>
            {detail.cases.length === 0 ? (
              <p className="px-5 py-4 text-small text-ink-muted">{t("admin.enterprises.noCases")}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border-subtle text-small">
                {detail.cases.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <Link
                      to={`/verification/${item.id}`}
                      className="font-medium text-ink hover:text-brand-600"
                    >
                      {formatDateTime(item.createdAt)}
                    </Link>
                    <StatusBadge
                      label={
                        item.decision === null
                          ? t("admin.queue.awaiting_review")
                          : t(`admin.case.${item.decision}`)
                      }
                      tone={
                        item.decision === null
                          ? "progress"
                          : item.decision === "approved"
                            ? "done"
                            : item.decision === "rejected"
                              ? "problem"
                              : "attention"
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        {canChange && (
          <Panel
            title={t(
              enterprise.suspension === null
                ? "admin.enterprises.suspendTitle"
                : "admin.enterprises.reinstateTitle",
            )}
            description={t(
              enterprise.suspension === null
                ? "admin.enterprises.suspendIntro"
                : "admin.enterprises.reinstateIntro",
            )}
          >
            <form className="flex flex-col gap-3" onSubmit={(event) => void change(event)}>
              <Field
                id="suspension-reason"
                label={t("admin.enterprises.reason")}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
              />
              {error !== undefined && <ErrorText>{error}</ErrorText>}
              <Button
                type="submit"
                variant={enterprise.suspension === null ? "danger" : "primary"}
                disabled={reason.trim().length < 3}
              >
                {t(
                  enterprise.suspension === null
                    ? "admin.enterprises.suspend"
                    : "admin.enterprises.reinstate",
                )}
              </Button>
            </form>
          </Panel>
        )}
      </div>
      {stepUp.dialog}
    </section>
  );
}

/**
 * The enterprise's last sanctions screening, and a new one (ADR 0026): against every list at least every 90 days, and
 * whenever a list changes. A match that should stop the enterprise is a suspension, decided apart.
 */
function ScreeningPanel({ enterprise, onRecorded }: { enterprise: EnterpriseRow; onRecorded: () => void }) {
  const t = useTranslations();
  const { session } = useSession();
  const [draft, setDraft] = useState<ScreeningDraft>(EMPTY_SCREENING);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const last = enterprise.screening ?? null;

  const record = async (event: FormEvent) => {
    event.preventDefault();
    if (!screeningReady(draft)) return;
    setBusy(true);
    setError(undefined);
    try {
      const { data, error: problem } = await session.api.POST(
        "/v1/ops/enterprises/{enterpriseId}/screenings",
        {
          params: { path: { enterpriseId: enterprise.id } },
          body: screeningBody(draft),
        },
      );
      if (data === undefined) return setError(t(errorKey(problem)));
      setDraft(EMPTY_SCREENING);
      onRecorded();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={t("admin.screening.title")} description={t("admin.screening.intro")}>
      <form className="flex flex-col gap-4" onSubmit={(event) => void record(event)}>
        <p className="text-small text-ink">
          {last === null
            ? t("admin.screening.never")
            : t("admin.screening.lastValue", {
                result: t(`admin.screening.${last.result}`),
                date: formatDateTime(last.at),
              })}
        </p>
        <ScreeningFields id="enterprise-screening" value={draft} onChange={setDraft} />
        {error !== undefined && <ErrorText>{error}</ErrorText>}
        <Button
          type="submit"
          variant="secondary"
          className="self-start"
          disabled={busy || !screeningReady(draft)}
        >
          {t("admin.screening.record")}
        </Button>
      </form>
    </Panel>
  );
}
